// THE ROUTING TABLE — the twelve routes, their addresses, their titles and the nav blocks the side nav and
// the phone tab bar are built from. One line per route, nothing else (T-321 / BUG-155).
//
// **Why it is its own file.** `routes.manifest.ts` is the WEB's contract with QA (ARCH §9.5, TEST_PLAN §1.5):
// every reachable test id, every overlay, every designed state and the switch that reaches it — about 2,300
// lines of declaration, which the build emits as `routes.manifest.json` and the suites read. The ROUTER
// (`app/router.tsx`) and the NAV (`app/shell/ShellNav.tsx`) need six fields of it: `id`, `path`, `title`,
// `shell`, `nav` and `arrivesIn`. Because they imported the manifest, the whole declaration sat in the app's
// first chunk — 23.8 KB of the eager chunk's generated bytes, plus the module-init cost of `route()` building
// a `Set` per entry over some 1,500 id strings — downloaded, compiled and executed before the first paint by
// every reader, to produce a nav of seven items. REQ-280 AC1/AC3: it does not belong there.
//
// **There is still ONE source of truth.** This file is it, and `routes.manifest.ts` is built FROM it —
// `route('home', { … })` looks its address and nav block up here and throws on an id this table does not
// carry, so "a route cannot exist without being listed" (DESIGN_SYSTEM §10's "MVP never shows a dead link",
// enforced by construction) is unchanged, and so is "the router is BUILT from this list". Nothing is
// duplicated and nothing can drift: the manifest cannot describe a route this table does not have, and
// `routes.table.test.ts` fails if the table carries one the manifest never declares.
//
// Pure data: no React, no DOM, no imports — `vite.config.ts` and Node tools load it (through the manifest)
// directly.

/**
 * A route that is also a destination in the global nav (T-036). The nav is BUILT from these: a destination
 * cannot exist without a route behind it, which is how "MVP never shows a dead link" (DESIGN_SYSTEM §10) is
 * enforced by construction. A screen's task adds its route here with its `nav` block and the nav grows —
 * the order, the label, the icon and the phone tab bar all come from this one place.
 */
export interface RouteNav {
  /** Position in the nav, low first. */
  readonly order: number;
  /** Nav label — a §7 glossary word, never a Meta term. */
  readonly label: string;
  /** Label in the phone tab bar when the nav label is too long ("Spend & Budgets" → "Spend"). */
  readonly shortLabel?: string;
  /** An `ICON_NAMES` member of `ui/Icon` (a test keeps the two in step; this file imports nothing). */
  readonly icon: string;
  /** `main` = the destination list; `footer` = pinned to the bottom of the nav (Settings). */
  readonly group?: 'main' | 'footer';
  /**
   * In the phone tab bar below 768 px (DESIGN_SYSTEM §10). FOUR destinations are primary, not five: the
   * fifth slot is "More", which is not a destination at all but the sheet that gathers the ones the tab bar
   * does not carry (§11 — "everything else lives under More"). See `ShellTabBar`.
   */
  readonly primary?: boolean;
  /** A count badge riding on the item: the only one in MVP is the unread alerts count (`shell.alerts-badge`). */
  readonly badge?: 'unreadAlerts';
}

/** One route, as the router and the nav know it. `routes.manifest.ts` adds everything QA knows. */
export interface RouteTableEntry {
  /** Stable id; also the key of the lazy module in app/router.tsx and of the entry in routes.manifest.ts. */
  readonly id: string;
  /** React Router path. Hash routing: the browser URL is `#<path>` (ARCH §9.2). */
  readonly path: string;
  /** Document title, without the product suffix. */
  readonly title: string;
  /** Whether the global shell (nav, mode chip, date control) frames the route. Welcome and first-run: no (REQ-006 AC5). */
  readonly shell: boolean;
  /** Present when the route is a nav destination; absent for detail pages reached from a list. */
  readonly nav?: RouteNav;
  /**
   * Placeholder routes only (T-037): the task that replaces the "arrives in" page with the real screen.
   * The nav is built from this list, so a destination cannot exist without a route; this is the other half —
   * the route a destination points at lands somewhere honest until its screen is written. No entry carries
   * one today (T-170 drew the last of them); the field and its checks stay, because they are what would
   * catch a slot added back without the task that owns it.
   */
  readonly arrivesIn?: string;
}

/**
 * The twelve routes, in manifest order. The ORDER of this array is the manifest's order and the order
 * `app/router.tsx` builds the route objects in; the nav's order is `nav.order`, never this.
 */
export const routesTable: readonly RouteTableEntry[] = [
  { id: 'welcome', path: '/', title: 'Welcome', shell: false },
  { id: 'signup', path: '/signup', title: 'Start your free trial', shell: false },
  { id: 'login', path: '/login', title: 'Log in', shell: false },
  { id: 'account', path: '/account', title: 'Your account', shell: false },
  { id: 'terms', path: '/terms', title: 'Terms of service', shell: false },
  { id: 'privacy', path: '/privacy', title: 'Privacy policy', shell: false },
  {
    id: 'home',
    path: '/home',
    title: 'Home',
    shell: true,
    nav: { order: 1, label: 'Home', icon: 'home', group: 'main', primary: true },
  },
  {
    id: 'campaigns',
    path: '/campaigns',
    title: 'Campaigns',
    shell: true,
    nav: { order: 2, label: 'Campaigns', icon: 'layers', group: 'main', primary: true },
  },
  { id: 'entity/:id', path: '/entity/:id', title: 'Detail', shell: true },
  {
    id: 'spend/pacing',
    path: '/spend/pacing',
    title: 'Spend & Budgets',
    shell: true,
    nav: {
      order: 3,
      label: 'Spend & Budgets',
      shortLabel: 'Spend',
      icon: 'wallet',
      group: 'main',
      primary: true,
    },
  },
  { id: 'spend/allocation', path: '/spend/allocation', title: 'Spend & Budgets', shell: true },
  {
    id: 'creatives',
    path: '/creatives',
    title: 'Creatives',
    shell: true,
    nav: { order: 4, label: 'Creatives', icon: 'images', group: 'main' },
  },
  {
    id: 'alerts',
    path: '/alerts',
    title: 'Alerts',
    shell: true,
    nav: {
      order: 5,
      label: 'Alerts',
      icon: 'bell',
      group: 'main',
      primary: true,
      badge: 'unreadAlerts',
    },
  },
  { id: 'alerts/:alertId', path: '/alerts/:alertId', title: 'Alert', shell: true },
  { id: 'alerts/rules', path: '/alerts/rules', title: 'Alert rules', shell: true },
  {
    id: 'activity',
    path: '/activity',
    title: 'Activity',
    shell: true,
    nav: { order: 6, label: 'Activity', icon: 'history', group: 'main' },
  },
  {
    id: 'settings',
    path: '/settings',
    title: 'Settings',
    shell: true,
    nav: { order: 7, label: 'Settings', icon: 'settings', group: 'footer' },
  },
];

/** The entry for `id`, or `undefined`. `routes.manifest.ts` throws on the `undefined`. */
export function routeTableEntry(id: string): RouteTableEntry | undefined {
  return routesTable.find((entry) => entry.id === id);
}

/**
 * A route id as ONE test-id segment (D-019): `spend/pacing` → `spend-pacing`. Ids are paths, test ids are
 * dotted segments, so the slash has to go — `shell.nav.spend/pacing` would read as two segments. A path
 * parameter goes with it (`entity/:id` → `entity`): a detail page is reached from a list, never from the
 * nav, so its slug names the route family rather than one entity (T-046).
 */
export function routeSlug(routeId: string): string {
  return routeId.replace(/\/:[^/]+/g, '').replace(/\//g, '-');
}

/** A route that carries a nav block — the shape the nav iterates. */
export type NavDestination = RouteTableEntry & { readonly nav: RouteNav };

/** The nav destinations, in nav order: `main` first, then the `footer` group pinned to the bottom. */
export function navDestinations(group?: RouteNav['group']): readonly NavDestination[] {
  return routesTable
    .filter((entry): entry is NavDestination => entry.nav !== undefined)
    .filter((entry) => group === undefined || (entry.nav.group ?? 'main') === group)
    .slice()
    .sort((a, b) => a.nav.order - b.nav.order);
}

/** The destinations of the phone tab bar below 768 px, in nav order (DESIGN_SYSTEM §10). */
export function tabBarDestinations(): readonly NavDestination[] {
  return navDestinations().filter((entry) => entry.nav.primary);
}
